// Draw the focus ring inside the element when an overflow ancestor would clip it
const CLIPS = /hidden|clip|auto|scroll/;

// Per-element facts that don't change; measured once, before any ring class applies
const reachCache = new WeakMap();
const overlayCache = new WeakMap();

// Outer reach of all box-shadows, e.g. "rgb(…) 0px 0px 5px 4px, …"
const shadowReach = (value) => {
  if (!value || value === 'none') return 0;
  return value.split(/,(?![^(]*\))/).reduce((max, shadow) => {
    if (/\binset\b/.test(shadow)) return max;
    const [x = 0, y = 0, blur = 0, spread = 0] = (shadow.match(/-?[\d.]+px/g) ?? []).map(parseFloat);
    return Math.max(max, Math.max(Math.abs(x), Math.abs(y)) + blur + spread);
  }, 0);
};

const ringReach = (el) => {
  if (!reachCache.has(el)) {
    const { outlineStyle, outlineOffset, outlineWidth, boxShadow } = getComputedStyle(el);
    const outline = outlineStyle === 'none' ? 0 : parseFloat(outlineOffset) + parseFloat(outlineWidth);
    reachCache.set(el, Math.max(outline, shadowReach(boxShadow)));
  }
  return reachCache.get(el);
};

// An ::after overlay keeps the ring above positioned children (e.g. image cards)
const overlayClasses = (el) => {
  if (!overlayCache.has(el)) {
    const { display, position } = getComputedStyle(el);
    let classes = [];
    if (display !== 'inline' && getComputedStyle(el, '::after').content === 'none') {
      if (position !== 'static') {
        classes = ['focus-ring-overlay'];
      } else {
        // Becoming relative must not re-anchor absolutely positioned descendants
        const reanchors = [...el.querySelectorAll('*')].some((child) => {
          const pos = getComputedStyle(child).position;
          return (pos === 'absolute' || pos === 'fixed') && !el.contains(child.offsetParent);
        });
        if (!reanchors) classes = ['focus-ring-overlay', 'focus-ring-anchor'];
      }
    }
    overlayCache.set(el, classes);
  }
  return overlayCache.get(el);
};

const isClipped = (el, reach) => {
  const box = el.getBoundingClientRect();
  for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(parent);
    const clipX = CLIPS.test(overflowX);
    const clipY = CLIPS.test(overflowY);
    if (!clipX && !clipY) continue;

    const pb = parent.getBoundingClientRect();
    const left = pb.left + parent.clientLeft;
    const top = pb.top + parent.clientTop;
    if (clipX && (box.left - reach < left || box.right + reach > left + parent.clientWidth)) return true;
    if (clipY && (box.top - reach < top || box.bottom + reach > top + parent.clientHeight)) return true;
  }
  return false;
};

document.addEventListener('focusin', ({ target: el }) => {
  // Mouse focus shows no ring, so skip the measuring
  if (!(el instanceof Element) || !el.matches(':focus-visible')) return;

  // Read everything first, then write classes once
  const reach = ringReach(el);
  const clipped = reach > 0 && isClipped(el, reach);
  const overlay = clipped ? overlayClasses(el) : [];

  el.classList.toggle('focus-ring-inset', clipped);
  el.classList.toggle('focus-ring-overlay', overlay.includes('focus-ring-overlay'));
  el.classList.toggle('focus-ring-anchor', overlay.includes('focus-ring-anchor'));
});
